import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/api";
import Shell from "@/components/Shell";
import { Spinner, Overline, Stat } from "@/components/Brand";
import { useAuth } from "@/context/AuthContext";
import PopularPieces from "@/components/PopularPieces";
import { LayoutDashboard, ScanLine, Users2, BookOpen, CalendarCheck, QrCode, UserCog, Flame, Store, TrendingUp, Sparkles } from "lucide-react";

export const exhibitorNav = [
  { key: "home", to: "/exhibitor", label: "Home", icon: LayoutDashboard },
  { key: "scan", to: "/exhibitor/scan", label: "Scan", icon: ScanLine },
  { key: "leads", to: "/exhibitor/leads", label: "Leads", icon: Users2 },
  { key: "catalogues", to: "/exhibitor/catalogues", label: "Catalogues", icon: BookOpen },
  { key: "networking", to: "/exhibitor/networking", label: "Network", icon: Users2 },
  { key: "followups", to: "/exhibitor/followups", label: "Tasks", icon: CalendarCheck },
];

export default function ExhibitorDashboard() {
  const [a, setA] = useState(null);
  const [profile, setProfile] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    api.get("/exhibitor/analytics").then(({ data }) => setA(data));
    api.get("/exhibitor/profile").then(({ data }) => setProfile(data));
  }, []);

  if (!a || !profile) return <Shell nav={exhibitorNav}><Spinner /></Shell>;

  const quick = [
    { label: "My QR Code", icon: QrCode, to: "/exhibitor/qr", key: "qr" },
    { label: "Staff", icon: UserCog, to: "/exhibitor/staff", key: "staff" },
    { label: "Products", icon: Store, to: "/exhibitor/products", key: "products" },
    { label: "Enquiries", icon: BookOpen, to: "/exhibitor/enquiries", key: "enquiries" },
    { label: "Networking", icon: Users2, to: "/exhibitor/networking", key: "networking" },
  ];

  return (
    <Shell nav={exhibitorNav}>
      <Overline>Exhibitor Dashboard</Overline>
      <div className="flex items-center justify-between mt-2">
        <h1 className="font-display text-3xl gold-text">{profile.name}</h1>
        {!profile.activated && (
          <span className="text-[10px] uppercase tracking-wide px-2 py-1 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30">Setup pending</span>
        )}
      </div>
      <p className="text-slate-400 text-sm mt-1">{profile.hall} · {profile.booth} · {profile.city}</p>

      <div className="grid grid-cols-3 gap-4 mt-6">
        <Stat testid="stat-leads" label="Total Leads" value={a.total_leads} icon={Users2} />
        <Stat testid="stat-hot" label="Hot Leads" value={a.hot_leads} icon={Flame} />
        <Stat testid="stat-checkins" label="Check-ins" value={a.checkins} icon={TrendingUp} />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
        <Stat testid="stat-saves" label="Catalogue Saves" value={a.catalogue_saves} icon={BookOpen} />
        <Stat testid="stat-pending" label="Pending Tasks" value={a.pending_followups} icon={CalendarCheck} />
        <div className="glass rounded-2xl p-4 col-span-2 flex items-center justify-between" data-testid="stat-credits">
          <div>
            <div className="text-xs text-slate-400 uppercase tracking-wide">AI Credits</div>
            <div className="font-display text-3xl mt-1 gold-text">{a.ai_credits}</div>
          </div>
          <Sparkles size={28} className="text-amber-400/70" />
        </div>
      </div>

      {<PopularPieces />}

      <h2 className="font-display text-2xl gold-text mt-8">Quick Actions</h2>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
        {quick.map((q) => (
          <button key={q.key} data-testid={`quick-${q.key}`} onClick={() => navigate(q.to)} className="glass rounded-2xl p-5 card-lift text-left">
            <div className="h-11 w-11 rounded-xl btn-gold flex items-center justify-center"><q.icon size={20} className="text-[#0B0F12]" /></div>
            <div className="text-slate-100 font-medium mt-3">{q.label}</div>
          </button>
        ))}
      </div>
    </Shell>
  );
}
